// `/notes` — the inbox first, then everything.
//
// THE INBOX IS THE POINT OF THIS SCREEN. A note whose deal the notetaker could
// not work out is stored unfiled rather than guessed at or dropped, and that
// decision only pays off if somebody can see the ones waiting. An inbox nobody
// looks at is the same as having dropped them, so it sits at the top, it says
// how many are waiting, and filing one is a single control on the row.

import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import type { NoteListItem } from "@crm/shared";
import styles from "./NotesPage.module.css";
import { Button } from "../../design/Button.js";
import { Card, CardHeader } from "../../design/Card.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { usePageChrome } from "../../design/pageChrome.js";
import { NoteList } from "./NoteList.js";
import { useDealOptions, useNotes, useUnfiledNotes } from "../../api/queries.js";
import { useNoteMutations } from "../../api/mutations.js";

/**
 * File one note onto a deal.
 *
 * A plain select, because there are nine deals and a combobox for nine options
 * is ceremony. It sends `If-Match` with the version the row was LOADED at, like
 * every other write here — two people filing the same note is a 409 rather than
 * a silent last-writer-wins.
 */
function FileToDeal({ note }: { note: NoteListItem }): ReactNode {
  const deals = useDealOptions();
  const { update } = useNoteMutations();
  const [dealId, setDealId] = useState("");

  return (
    <div className={styles.file}>
      <select
        className={styles.select}
        aria-label={`File “${note.title ?? "note"}” to a deal`}
        value={dealId}
        onChange={(event) => setDealId(event.target.value)}
      >
        <option value="">Choose a deal…</option>
        {(deals.data?.items ?? []).map((deal) => (
          <option key={deal.id} value={deal.id}>
            {deal.customer_name}
          </option>
        ))}
      </select>
      <Button
        small
        disabled={dealId === "" || update.isPending}
        onClick={() =>
          update.mutate({ id: note.id, version: note.version, body: { deal_id: dealId } })
        }
      >
        File
      </Button>
    </div>
  );
}

export function NotesPage(): ReactNode {
  const { note: openNoteId } = useSearch({ from: "/notes" });
  const navigate = useNavigate();
  const unfiled = useUnfiledNotes();
  const all = useNotes({ limit: 100 });

  const waiting = unfiled.data?.total ?? 0;
  usePageChrome({
    title: "Notes",
    ...(unfiled.data === undefined
      ? {}
      : { summary: waiting === 0 ? "nothing unfiled" : `${waiting} unfiled` }),
  });

  return (
    <div className={styles.page}>
      <Card>
        <CardHeader
          title="Unfiled"
          description="Notes whose deal has not been determined. They are searchable while they wait; filing one is a single choice."
          actions={
            waiting > 0 ? (
              <Button
                small
                onClick={() => void navigate({ to: "/search", search: { q: "" } })}
                title="Search every note"
              >
                Search notes
              </Button>
            ) : undefined
          }
        />
        <QueryBoundary query={unfiled} label="the unfiled notes">
          {(data) => (
            <NoteList
              notes={data.items}
              initiallyOpen={openNoteId ?? null}
              rowActions={(note) => <FileToDeal note={note} />}
              empty="Nothing waiting — every note is on a deal."
            />
          )}
        </QueryBoundary>
      </Card>

      <Card>
        <CardHeader
          title="All notes"
          description="Every note, newest meeting first. A note with no recorded date sorts last rather than first."
        />
        <QueryBoundary query={all} label="the notes">
          {(data) => (
            <NoteList
              notes={data.items}
              initiallyOpen={openNoteId ?? null}
              empty="No notes yet. The notetaker writes them here, and so can you."
            />
          )}
        </QueryBoundary>
      </Card>
    </div>
  );
}
